import Info from '@mui/icons-material/Info';
import { FormBlock, FormCard, Toggle } from '@qbe/ads-blocks-forms';
import {
  Alert,
  Box,
  Button,
  Dropdown,
  FormGroupLabel,
  IconButton,
  RadioSelectableCard,
  RadioSelectableCardProps,
  Spacer,
  Spinner,
  ToggleProps,
  Typography,
} from '@qbe/ads-core';
import React, { useEffect, useMemo, useState } from 'react';

import ExcessErrorModal from '~/components/ExcessErrorModal';
import InformationList from '~/components/InformationList';
import CurrencyField from '~/components-ds/CurrencyField';
import Modal from '~/components-ds/Modal';
import {
  CONSTRUCTION_BUILDING_OPTIONS,
  CONSTRUCTION_INFO_LIST,
  CONSTRUCTION_YEARS,
  CURRENCY_TEXTFIELD_LIMIT,
  EPS_INFO_LIST,
  EPS_OPTIONS,
} from '~/constants/domain-specific';
import { simpleQuestionOptions } from '~/constants/questions';
import {
  useAppContent,
  useAppDispatch,
  useCoverDetails,
  useCoverDetailsEffects,
  useQuote,
} from '~/hooks';
import { useAppNavigate } from '~/hooks/useAppNavigate';
import { useTrackOptimizeActivate } from '~/hooks/useTrackOptimizeActivate';
import {
  updateBuildingSI,
  updateConstruction,
  updateContentsSI,
  updateEPS,
  updateFlood,
  updateSprinkler,
  updateYearBuilt,
} from '~/slices/coverDetails';
import { addValidatedCover, removeValidatedCover } from '~/slices/selectCover';
import { AppDispatch } from '~/store';
import { qalFormCompleteAction, qalFormStartAction, qalTrackEventAction } from '~/thunks/analytics';
import { Cover } from '~/types/cover';
import { SimpleQuestionAnswer, SumInsuredType } from '~/types/enums';
import { isTruthy } from '~/utils';
import { isFormStartStarted } from '~/utils/analytics/trackUtils';

import { ContextualHelpWithButton } from '../../components-ds/ContextualHelp/ContextualHelpWithButton';

const YesNoBoolean = {
  [SimpleQuestionAnswer.Yes]: true,
  [SimpleQuestionAnswer.No]: false,
};

const getBuildingAndContentsSIZeroError = ({
  buildingSI,
  contentsSI,
}: {
  buildingSI: number | null;
  contentsSI: number | null;
}) => buildingSI === 0 && contentsSI === 0;

const trackEngagementEvent = (
  event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  type: SumInsuredType,
  dispatch: AppDispatch
) => {
  if (event === null) return;
  const value = event?.target?.value;
  if (value !== '0') return;
  dispatch(
    qalTrackEventAction({
      event: 'sum_insured',
      eventAction: `${type} - sum insured zero amount`,
      eventLabel: value,
    })
  );
};

const zeroDollarEngagementTracking = (
  event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  sumInsuredType: SumInsuredType,
  dispatch: AppDispatch
) => {
  trackEngagementEvent(event, sumInsuredType, dispatch);
};

const QuestionBuildingSI: React.FC = () => {
  const { getTextContent, getJSXContent } = useAppContent({
    basePath: 'dsme.coverDetailsBuildingAndContents',
  });

  const dispatch = useAppDispatch();
  const { contentsSI, buildingSI } = useCoverDetails();

  const buildingAndContentsSIZeroError = getBuildingAndContentsSIZeroError({
    buildingSI,
    contentsSI,
  });

  const handleFormStart = () => {
    if (!isFormStartStarted()) dispatch(qalFormStartAction());
  };

  // Tracks the "optimize.activate" event when the component mounts or re-renders.
  // This is used to notify Webtrends (or other analytics tools) for visibility tracking
  // and ensures dynamic content is properly handled.
  useTrackOptimizeActivate();

  return (
    <>
      <FormGroupLabel
        title={getTextContent('fields.sumInsuredBuilding.title')}
        titleSuffix={
          <ContextualHelpWithButton
            tracking={`help: ${getTextContent('fields.sumInsuredBuilding.title')}`}
          >
            <Typography>
              {getJSXContent('contextualhelpers.sumInsuredBuilding.description')}
            </Typography>
          </ContextualHelpWithButton>
        }
      />
      <FormBlock preset="input" maxWidth="100%">
        <CurrencyField
          id="buildingSI"
          name="building sum insured"
          label={getTextContent('fields.sumInsuredBuilding.label')}
          helperText={getTextContent('fields.sumInsuredBuilding.helperText')}
          data-track="Add Business Building sum insured"
          data-track-value="mask"
          errorText={getTextContent('fields.sumInsuredBuilding.invalid')}
          error={buildingAndContentsSIZeroError}
          defaultValue={buildingSI?.toString()}
          fieldValidation={{
            validations: {
              numericOnly: true,
              required: true,
              minValue: 0,
              maxLength: CURRENCY_TEXTFIELD_LIMIT,
            },
            action: updateBuildingSI,
          }}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            zeroDollarEngagementTracking(event, SumInsuredType.Building, dispatch);
            handleFormStart();
          }}
          required={!isTruthy(contentsSI)}
        />
      </FormBlock>
    </>
  );
};

const QuestionContentsSI = () => {
  const { getTextContent, getJSXContent } = useAppContent({
    basePath: 'dsme.coverDetailsBuildingAndContents',
  });

  const { contentsSI, buildingSI } = useCoverDetails();
  const dispatch = useAppDispatch();

  const buildingAndContentsSIZeroError = getBuildingAndContentsSIZeroError({
    buildingSI,
    contentsSI,
  });

  const handleFormStart = () => {
    if (!isFormStartStarted()) dispatch(qalFormStartAction());
  };

  return (
    <>
      <FormGroupLabel
        title={getTextContent('fields.sumInsuredContent.title')}
        titleSuffix={
          <ContextualHelpWithButton
            tracking={`help: ${getTextContent('fields.sumInsuredContent.title')}`}
          >
            <Typography>
              {getJSXContent('contextualhelpers.sumInsuredContent.description')}
            </Typography>
          </ContextualHelpWithButton>
        }
      />
      <FormBlock preset="input" maxWidth="100%">
        <CurrencyField
          id="contentsSI"
          name="contents sum insured"
          label={getTextContent('fields.sumInsuredContent.label')}
          helperText={getTextContent('fields.sumInsuredContent.helperText')}
          data-track="Add Business Contents sum insured"
          data-track-value="mask"
          errorText={getTextContent('fields.sumInsuredContent.invalid')}
          error={buildingAndContentsSIZeroError}
          defaultValue={contentsSI?.toString()}
          fieldValidation={{
            validations: {
              numericOnly: true,
              required: true,
              minValue: 0,
              maxLength: CURRENCY_TEXTFIELD_LIMIT,
            },
            action: updateContentsSI,
          }}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            zeroDollarEngagementTracking(event, SumInsuredType.Contents, dispatch);
            handleFormStart();
          }}
          required={!isTruthy(buildingSI)}
        />
      </FormBlock>
    </>
  );
};

const QuestionConstruction = () => {
  const { getTextContent: getSharedContent } = useAppContent({ basePath: 'shared' });
  const { getTextContent } = useAppContent({ basePath: 'dsme.coverDetailsBuildingAndContents' });

  const { construction } = useCoverDetails();
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();

  const [showModal, setShowModal] = useState(false);

  const radioList = useMemo(
    () =>
      CONSTRUCTION_BUILDING_OPTIONS.map(({ text, value }) => ({
        label: text,
        id: value,
        value,
        'data-track': getTextContent('fields.construction.title'),
        'data-track-value': 'capture',
      })) as RadioSelectableCardProps['radioList'],
    [getTextContent]
  );

  const handleFormStart = () => {
    if (!isFormStartStarted()) dispatch(qalFormStartAction());
  };

  return (
    <>
      <Modal
        id="construction-information-modal"
        open={showModal}
        title={getTextContent('fields.constructionModal.title')}
        primaryBtnProps={{
          label: getSharedContent('closeModalButtonLabel'),
          onClick: () => setShowModal(false),
          'data-track': getSharedContent('closeModalButtonLabel'),
        }}
        onClose={() => setShowModal(false)}
      >
        <InformationList list={CONSTRUCTION_INFO_LIST} />
      </Modal>

      <Box sx={{ maxWidth: '400px' }}>
        <RadioSelectableCard
          title={getTextContent('fields.construction.title')}
          titleSuffix={
            <IconButton
              onClick={() => setShowModal(true)}
              iconProps={{ muiIcon: Info }}
              data-track={`help: ${getTextContent('fields.construction.title')}`}
            />
          }
          name="construction"
          radioList={radioList}
          direction="vertical"
          onChange={(value) => {
            handleFormStart();
            if (Array.isArray(value)) {
              throw new Error('Not handled');
            }
            dispatch(updateConstruction(value as string));
            navigate('#eps');
          }}
          defaultValue={construction as string}
        />
      </Box>
    </>
  );
};

const QuestionEPS = () => {
  const { getTextContent: getSharedContent } = useAppContent({ basePath: 'shared' });
  const { getTextContent } = useAppContent({ basePath: 'dsme.coverDetailsBuildingAndContents' });

  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const { eps } = useCoverDetails();

  const [showModal, setShowModal] = useState(false);

  const handleFormStart = () => {
    if (!isFormStartStarted()) dispatch(qalFormStartAction());
  };

  return (
    <FormBlock preset="toggle" maxWidth="100%">
      <Toggle
        title={getTextContent('fields.eps.title')}
        titleSuffix={
          <IconButton
            onClick={() => setShowModal(true)}
            iconProps={{ muiIcon: Info }}
            data-track={`help: ${getTextContent('fields.eps.title')}`}
          />
        }
        data-track="eps"
        data-track-value="capture"
        name="eps"
        optionList={EPS_OPTIONS.map((o): ToggleProps['optionList'][number] => ({
          id: o.text,
          value: o.value,
          label: o.text,
          'data-track': 'eps',
          'data-track-value': 'capture',
          onChange: (e) => {
            handleFormStart();
            dispatch(updateEPS(e.target.value));
            navigate('#year-built');
          },
          defaultChecked: eps === o.value,
        }))}
      />

      <Modal
        id="eps-modal"
        open={showModal}
        title={getTextContent('fields.epsModal.title')}
        primaryBtnProps={{
          label: getSharedContent('closeModalButtonLabel'),
          onClick: () => setShowModal(false),
        }}
        onClose={() => setShowModal(false)}
      >
        <InformationList
          list={EPS_INFO_LIST}
          footerText={getTextContent('fields.epsModal.footer')}
        />
      </Modal>
    </FormBlock>
  );
};

const QuestionYearBuilt = () => {
  const { getTextContent } = useAppContent({
    basePath: 'dsme.coverDetailsBuildingAndContents.fields.yearBuilt',
  });

  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const { yearBuilt } = useCoverDetails();

  const handleFormStart = () => {
    if (!isFormStartStarted()) dispatch(qalFormStartAction());
  };

  const onChangeHandler = (value: string | string[]) => {
    if (Array.isArray(value)) {
      throw new Error('Not handled');
    }
    handleFormStart();
    dispatch(updateYearBuilt(value));
    navigate('#sprinklers');
  };

  return (
    <>
      <FormGroupLabel title={getTextContent('title')} />
      <FormBlock preset="dropdown">
        <Dropdown
          optionList={CONSTRUCTION_YEARS.map((o) => ({
            label: o.description,
            value: o.description,
            'data-track': getTextContent('title'),
            'data-track-value': 'capture',
          }))}
          onChange={(e) => onChangeHandler(e.target.value as Cover)}
          onBlur={(e) => onChangeHandler(e.target.value as Cover)}
          value={yearBuilt ?? ''}
          data-track-label="cover range"
          required
          label={getTextContent('label')}
          id="coverRange"
          name="coverRange"
        />
      </FormBlock>
    </>
  );
};

const QuestionSprinklers = () => {
  const { getTextContent, getJSXContent } = useAppContent({
    basePath: 'dsme.coverDetailsBuildingAndContents',
  });

  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();

  const { sprinkler } = useCoverDetails();

  const handleFormStart = () => {
    if (!isFormStartStarted()) dispatch(qalFormStartAction());
  };

  return (
    <FormBlock preset="toggle">
      <Toggle
        data-track="sprinklers"
        data-track-value="capture"
        title={getTextContent('fields.sprinklers.title')}
        titleSuffix={
          <ContextualHelpWithButton tracking={`help: ${getTextContent('fields.sprinklers.title')}`}>
            <Typography>{getJSXContent('contextualhelpers.sprinklers.description')}</Typography>
          </ContextualHelpWithButton>
        }
        name="sprinklers"
        optionList={simpleQuestionOptions.map((o): ToggleProps['optionList'][number] => ({
          id: `eps${o.text}`,
          value: o.value,
          label: o.text,
          onChange: (e) => {
            handleFormStart();
            const installed = e.target.value === SimpleQuestionAnswer.Yes;
            dispatch(updateSprinkler(installed));
            navigate('#flood');
          },
          defaultChecked: sprinkler === YesNoBoolean[o.value],
        }))}
      />
    </FormBlock>
  );
};

const QuestionFloodCover = () => {
  const { getTextContent, getJSXContent } = useAppContent({
    basePath: 'dsme.coverDetailsBuildingAndContents',
  });

  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const { flood } = useCoverDetails();

  const handleFormStart = () => {
    if (!isFormStartStarted()) dispatch(qalFormStartAction());
  };

  return (
    <FormBlock preset="toggle">
      <Toggle
        title={getTextContent('fields.floodCover.title')}
        titleSuffix={
          <ContextualHelpWithButton tracking={`help: ${getTextContent('fields.floodCover.title')}`}>
            <Typography>{getJSXContent('contextualhelpers.floodCover.description')}</Typography>
          </ContextualHelpWithButton>
        }
        data-track="flood-cover"
        data-track-value="capture"
        name="flood-cover"
        optionList={simpleQuestionOptions.map((o): ToggleProps['optionList'][number] => ({
          id: `flood${o.text}`,
          value: o.value,
          label: o.text,
          'data-track': 'flood-cover',
          'data-track-value': 'capture',
          onChange: (e) => {
            handleFormStart();
            const installed = e.target.value === SimpleQuestionAnswer.Yes;
            dispatch(updateFlood(installed));
            navigate('#next');
          },
          defaultChecked: flood === YesNoBoolean[o.value],
        }))}
      />
    </FormBlock>
  );
};

const CoverDetailsBuildingAndContents: React.FC = () => {
  const { getTextContent: getSharedContent } = useAppContent({ basePath: 'shared' });
  const { getTextContent, getJSXContent } = useAppContent();
  const { getTextContent: getBacText } = useAppContent({
    basePath: 'dsme.coverDetailsBuildingAndContents',
  });

  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const { levelOfExcess } = useQuote();
  const { isValidBuildingAndContents, buildingSI, contentsSI } = useCoverDetails();
  const [openExcessError, setOpenExcessError] = useState(false);
  const { quotePremiums, isLoading, buttonText } = useCoverDetailsEffects({
    cover: Cover.BuildingAndContents,
  });

  const buildingAndContentsSIZeroError = getBuildingAndContentsSIZeroError({
    buildingSI,
    contentsSI,
  });

  const handleFormComplete = () => {
    if (isFormStartStarted()) dispatch(qalFormCompleteAction());
  };

  const handleNext = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    handleFormComplete();
    (buildingSI && buildingSI < levelOfExcess!) || (contentsSI && contentsSI < levelOfExcess!)
      ? setOpenExcessError(true)
      : quotePremiums();
  };

  useEffect(() => {
    !isValidBuildingAndContents
      ? dispatch(removeValidatedCover(Cover.BuildingAndContents))
      : dispatch(addValidatedCover(Cover.BuildingAndContents));
  }, [dispatch, isValidBuildingAndContents]);

  return (
    <>
      <Spinner description={getSharedContent('loadingLabel')} loading={isLoading} />

      <FormCard variant="sectionTitle" title={getBacText('title')}>
        <Box textAlign="center">
          <Typography variant="h5" component="legend" gutterBottom>
            {getBacText('fields.sumInsured.title')}
          </Typography>
          <Typography>{getBacText('fields.sumInsured.description')}</Typography>
        </Box>

        <QuestionBuildingSI />

        <QuestionContentsSI />

        {buildingAndContentsSIZeroError && ( // TODO - convert to form validation
          <>
            <Alert
              heading=""
              body={getBacText('fields.sumInsured.sumInsuredBuildingContentZero')}
              variant="warning"
            />
            <Spacer size="lg" />
          </>
        )}

        <Box id="construction-building" data-testid="construction-building">
          <QuestionConstruction />
        </Box>
        <Box id="eps">
          <QuestionEPS />
        </Box>
        <Box id="year-built">
          <QuestionYearBuilt />
        </Box>

        <Box id="sprinklers">
          <QuestionSprinklers />
        </Box>
        <Box id="flood">
          <QuestionFloodCover />
        </Box>

        <FormBlock preset="button">
          <Button
            data-track={`${buttonText} - building and contents`}
            onClick={handleNext}
            disabled={!isValidBuildingAndContents || isLoading}
            label={buttonText}
          />
        </FormBlock>
      </FormCard>

      <ExcessErrorModal
        open={openExcessError}
        onContinue={() => {
          setOpenExcessError(false);
          navigate('#buildingSI');
        }}
        onCancel={() => {
          navigate(getTextContent('shared.qbeHome'));
        }}
      >
        <Typography>{getJSXContent('dsme.coverDetails.shared.excessError.description')}</Typography>
      </ExcessErrorModal>
    </>
  );
};

export default CoverDetailsBuildingAndContents;
